import { formatCurrency, formatHours } from "@activecollab/components";
import capitalize from "lodash/capitalize";
import moment from "moment";
import { stringifyUrl } from "query-string";
import i18n from "~/i18n";
import Angie from "./angie.jsx";
import { formatTime } from "./date.jsx";

export const encodeData = (data) => {
  return Object.keys(data)
    .map((key) => {
      return [key, data[key]].map(encodeURIComponent).join(`=`);
    })
    .join(`&`);
};

export const globalUrl = (url) => {
  if (/http:\/\/|https:\/\//.test(url)) {
    return;
  }

  return `${Angie.root_url}/${url}`;
};

export const urlFromPath = (path = "", backPath = "", rootUrl = Angie.root_url) => {
  if (path.length) {
    path = path.replace(/^\/+/, ""); // remove leading slashes
  }

  let url = rootUrl;

  if (path.length) {
    url = `${url}/${path}`;
  }

  if (backPath.length) {
    url = `${url}?back_path=${backPath}`;
  }

  return url;
};

export const resolvePublicInvoiceLink = (absoluteUrl) => {
  // eslint-disable-next-line compat/compat
  const params = new URL(absoluteUrl).searchParams;
  return stringifyUrl({
    url: `/s/invoice`,
    query: { number: params.get("number"), hash: params.get("hash") },
  });
};

export const decimalToMinutes = (value) => {
  return Angie.functions.decimalToMinutes(value);
};

/**
 * @deprecated
 */
export const decimalNumberToHours = (decimal, long = false, roundHours = false) => {
  return Angie.functions.hoursFormatted(Angie.functions.decimalNumberToHours(decimal), long, roundHours);
};

export const hoursToDecimal = (hours, minutes) => {
  return Angie.functions.hoursToDecimal(hours, minutes);
};

export const rawDecimalToHours = (decimal) => {
  return Angie.functions.decimalNumberToHours(decimal);
};

export const calculateTimeToEstimatePercentage = (trackedTime, estimatedTime) => {
  if (estimatedTime > 0) {
    return trackedTime > 0 ? Math.round((trackedTime / estimatedTime) * 100) : 0;
  }
  return null;
};

export const hoursFormatted = (hours_object, long = false, roundHours = false) => {
  return Angie.functions.hoursFormatted(hours_object, long, roundHours);
};

export const validateWorkWeekHours = (value, min = 0) => {
  if (typeof value !== "number") {
    return false;
  }
  const regexIncrementQuarter =
    /^(0|[1-9]\d*)((\.([0-9]*25|[0-9]*50|[0-9]*75))|(,([0-9]*25|[0-9]*50|[0-9]*75)))?([,.]5)?$/;

  return value >= min && value <= 24 && regexIncrementQuarter.test(value);
};

export const preventNegativeAndEKeyPress = (e) => {
  if (e.key === "-" || e.key === "e") {
    e.preventDefault();
  }
};

export const formatTrackedAndEstimatedTime = (trackedTime = 0, estimatedTime = 0) => {
  if (estimatedTime > 0) {
    return `${formatHours(trackedTime ?? 0, false, false, "short")}/${formatHours(
      estimatedTime ?? 0,
      false,
      false,
      "short"
    )}`;
  } else {
    return formatHours(trackedTime ?? 0, false, false, "short");
  }
};

export const sortCompare = (a, b) => {
  if (a === b) {
    return 0;
  }
  return a < b ? -1 : 1;
};

export const descSortCompare = (a, b) => {
  if (a === b) {
    return 0;
  }
  return a < b ? 1 : -1;
};

export const prepareThumbnailUrl = (
  url,
  width = 40,
  height = width,
  scale = `crop`,
  thumb_token = "",
  download_token = ""
) => {
  if (!url) {
    return url;
  }

  return url
    .replace(`--WIDTH--`, width)
    .replace(`--HEIGHT--`, height)
    .replace(`--SCALE--`, scale)
    .replace("--THUMBNAIL-TOKEN--", thumb_token)
    .replace("--DOWNLOAD-TOKEN--", download_token);
};

export const timeValidator = (value) => {
  return /^([0-9]+)?(((:([0-5][0-9])?)|(:[0-5]?))|(\.[0-9]{0,10})|(,[0-9]{0,10}))?$/g.test(value);
};

export const isTimeValueValid = (value) => {
  if ([`.`, `:`].indexOf(value) !== -1) return false;
  if (parseFloat(value.replace(/\.|:/gi, ``)) === 0) return false;
  return value.length !== 0;
};

export const stopwatchEditValidator = (value) => /^([0-9]{0,2})?(((:([0-5][0-9])?)|(:[0-9]?)))?$/g.test(value);

export const truncateString = (string, maxLength) => {
  let concatString = string.substring(0, maxLength);
  if (string.length > maxLength) {
    concatString += `...`;
  }
  return concatString;
};

// http://www.jacklmoore.com/notes/rounding-in-javascript/
export const roundNumber = (number, decimals = 2) => {
  return Number(`${Math.round(`${number}e${decimals}`)}e-${decimals}`);
};

export const checkoutProcessStepUrl = (step) => {
  if (step === `payment_method`) {
    return `/subscription/payment-method`;
  }
  return `/subscription/billing-address`;
};

export const formatFileSize = (value, decimals = 2, trim_zeros = false) => {
  let data = {
    TB: 1099511627776,
    GB: 1073741824,
    MB: 1048576,
    kB: 1024,
  };

  let bytes, in_unit;

  for (let unit in data) {
    bytes = data[unit];
    in_unit = value / bytes;
    if (in_unit > 0.9) {
      let val = in_unit.toFixed(decimals);

      if (trim_zeros) {
        return `${parseFloat(val)}${unit}`;
      }

      return `${val}${unit}`;
    }
  }

  return `${parseInt(value)}B`;
};

export const daysBetween = (a, b) => {
  a = moment.unix(a);
  b = moment.unix(b);

  return a.diff(b, `days`);
};

// this function returns tax with two decimals, if passed value is decimal
// else it returns the passed integer
export const roundAmount = (amount) => {
  amount = Number(amount);
  const result = Number.isInteger(amount) ? `${amount}` : amount.toFixed(2);

  return result.replace(`.00`, ``);
};

export const removeSpaces = (text) => {
  if (typeof text !== `string`) {
    throw new TypeError(`removeSpaces expected string, received: ${typeof text}`);
  }
  return text.replace(/\s+/g, ``);
};

export const containsOnlyNumbers = (text) => {
  if (typeof text !== `string`) {
    throw new TypeError(`containsOnlyNumbers expected string, received: ${typeof text}`);
  }
  return /^\d+$/.test(text);
};

export const removeNonAlphanumeric = (text) => {
  if (typeof text !== `string`) {
    throw new TypeError(`removeAlphanumeric expected string, received: ${typeof text}`);
  }
  return text.replace(/[^a-z0-9]/gi, ``);
};

export const makeGanttLinkId = (source, target) => {
  source = parseInt(source);
  target = parseInt(target);

  return `${source}${target}${source + target}`;
};

export const moneyValidator = (value) => {
  const regex = /^(?=.*[0-9])\d{1,10}(\.[0-9]{0,2})?$/;
  return regex.test(value);
};

export const isEnglishLanguage = () => Angie.loaded_language === `en_US.UTF-8`;

/**
 * @deprecated use formatCurrency or formatNumber from @activecollab/components
 * @param number
 * @param decimals
 * @param dec_point
 * @param thousands_sep
 * @returns {string}
 */
export const formatNumber = (number, decimals, dec_point, thousands_sep) => {
  const n = !isFinite(+number) ? 0 : +number;
  const prec = !isFinite(+decimals) ? 0 : Math.abs(decimals);
  const sep = typeof thousands_sep === `undefined` ? `,` : thousands_sep;
  const dec = typeof dec_point === `undefined` ? `.` : dec_point;
  const toFixedFix = (n, prec) => {
    // Fix for IE parseFloat(0.55).toFixed(0) = 0;
    let k = Math.pow(10, prec);
    return Math.round(n * k) / k;
  };

  let s = (prec ? toFixedFix(n, prec) : Math.round(n)).toString().split(`.`);

  if (s[0].length > 3) {
    s[0] = s[0].replace(/\B(?=(?:\d{3})+(?!\d))/g, sep);
  }

  if (s.length === 1) {
    return s[0];
  }

  if ((s[1] || ``).length < prec) {
    s[1] = s[1] || ``;
    s[1] += new Array(prec - s[1].length + 1).join(`0`);
  }

  return s.join(dec);
};

export const timeToDecimal = (time, round = true) => {
  let [hours, minutes] = time.split(":");

  if (!hours) hours = 0;
  hours = parseInt(hours);
  minutes = parseInt(minutes);

  if (!minutes) return hours;

  if (minutes >= 60) {
    hours += Math.floor(minutes / 60);
    minutes = minutes % 60;
  }

  if (round) return parseFloat((hours + minutes / 60).toFixed(2));
  return parseFloat(hours + minutes / 60);
};

export const timeToMinutes = (time) => {
  let [hours, minutes] = time.split(":");
  if (!hours) hours = 0;
  if (!minutes) minutes = 0;
  hours = parseInt(hours);
  minutes = parseInt(minutes);

  return hours * 60 + minutes;
};

export const roundByIncrements = (number, increment = 1, offset = 0) => {
  if (number === 0) return increment;
  return Math.ceil((number - offset) / increment) * increment + offset;
};

export const toDecimal = (value) => {
  if (!value) return "0";
  if (value.indexOf(".") > -1) {
    const parsedValue = parseFloat(value);
    if (isNaN(parsedValue)) return "0";
    return `${parsedValue}`;
  }
  const parsedValue = timeToDecimal(value);
  return `${parsedValue}`;
};

export const toHours = (value, long = false, roundHours = false) => {
  if (!value) return "";
  if (value.indexOf(":") > -1) {
    const [hours, minutes] = value.split(":");
    const hoursObject = {
      hours: parseInt(hours) ? parseInt(hours) : 0,
      minutes: parseInt(minutes) ? parseInt(minutes) : 0,
    };
    return hoursFormatted(hoursObject);
  }
  const parsedValue = decimalNumberToHours(value, long, roundHours);
  return `${parsedValue}`;
};

export const minutesToHours = (minutes, noMinutes = false) => {
  const hours = minutes / 60;
  const rhours = Math.floor(hours);
  minutes = (hours - rhours) * 60;
  const rminutes = Math.round(minutes);
  if (noMinutes && rminutes === 0) {
    return toHours(`${rhours}`, false, true);
  }
  return toHours(rhours + ":" + rminutes);
};
export const minutesToHoursDecimal = (minutes) => {
  return minutes / 60;
};

export const dispatchToElectron = (name, message) => {
  Angie.functions.sendElectronAppMessage(name, message);
};

export const sendStopwatchElectronMessage = (elapsedTime) => {
  if (document.documentElement.classList.contains(`electron_app_win`)) {
    dispatchToElectron("stopwatch", i18n.t("Open ActiveCollab"));
  } else {
    let [hours, minutes] = formatTime(elapsedTime);
    dispatchToElectron(`stopwatch`, `${hours}:${minutes}`);
  }
};

export const getValFromTagAttribute = (sourceStr, attributeName = "src", maxLength = 24) => {
  const strRegExp = new RegExp(`${attributeName}="[^"]*`, "g");
  const attRegExp = new RegExp(/".*/, "g");

  const attributes = sourceStr.match(strRegExp);

  if (attributes) {
    const matches = attributes.map((attribute) => attribute.match(attRegExp));
    const strippedMatches = matches.map((match) => match[0].replace(/"/g, ""));
    return strippedMatches.filter((match) => match.length < maxLength);
  }

  return [];
};

export const isElectronApp = (checkDev = false) => {
  const appString = checkDev ? "ActiveCollabDev" : "ActiveCollab";

  if (navigator && navigator.userAgent) {
    return navigator.userAgent.indexOf(appString) > -1 && navigator.userAgent.indexOf("Electron") > -1;
  }

  return false;
};

export const isValidEmail = (email) => {
  let re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email) && !email.trim().endsWith("@example.com");
};

export const getHashUrl = (type, id, projectId) => {
  return `${capitalize(type)}-${id}-${projectId}`;
};

const object_property_value = (object, property) => {
  if (!object || !property) return undefined;

  const path = property.split(".");
  let result = object;

  for (const index in path) {
    result = result[path[index]];

    if (result === undefined) {
      return undefined;
    }
  }

  return result;
};

export const groupByDate = (items, date_key) => {
  let key,
    index,
    group,
    result = [],
    map = {};

  items.map((item) => {
    const date_value = object_property_value(item, date_key);
    const time = moment.utc(date_value * 1000);
    key = time.clone().local().format("MMM DD, YYYY");
    index = map[key];

    if (index === undefined) {
      group = {
        name: key,
        timestamp: Number(moment.utc(time).startOf("day").format("X")),
        iso: key,
        _items: [],
      };

      result.push(group);

      map[key] = result.length - 1;
    } else {
      group = result[index];
      group.timestamp = Number(moment.utc(time).startOf("day").format("X"));
    }

    group._items.push(item);
  });

  return result.sort((a, b) => b.timestamp - a.timestamp);
};

export const arraysEqual = (a, b) => {
  if (Array.isArray(a) && Array.isArray(b)) {
    const sortedA = a.sort(sortCompare);
    const sortedB = b.sort(sortCompare);
    return sortedA.length === sortedB.length && sortedA.every((val, index) => val === sortedB[index]);
  }
  return false;
};

export const calculatePercentForProgress = (open, completed) => {
  if (open === 0 && completed === 0) return 0;
  return open === 0 ? 100 : Math.floor((100 * completed) / (open + completed));
};

// 210.110 – must have 4 octets
// 255 – must have 4 octets
// y.y.y.y – only digits are allowed
// 255.0.0.y – only digits are allowed
// 666.10.10.20 – octet number must be between [0-255]
// 4444.11.11.11 – octet number must be between [0-255]
// 33.3333.33.3 – octet number must be between [0-255]
export const validateIP = (address) => {
  return /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(
    address
  );
};

export const calculateBudgetVsCost = (budget, project) => {
  budget.currency_id = project.currency_id;
  if (budget.budget) {
    budget.percentage = Math.round((budget.billable_cost_so_far / budget.budget) * 100 * 100) / 100;
  } else {
    budget.percentage = 100;
  }

  budget.billable_hours_so_far = 0;
  budget.not_billable_hours_so_far = 0;

  if (budget.cost_by_job_type) {
    budget.cost_by_job_type.forEach((cost_by_job_type) => {
      budget.billable_hours_so_far += cost_by_job_type.hours;
      budget.not_billable_hours_so_far += cost_by_job_type.non_billable_hours;
    });
  }

  budget.total_expense_so_far = budget.expenses + budget.non_billable_expenses;
  budget.total_hours_so_far = budget.billable_hours_so_far + budget.not_billable_hours_so_far;

  return budget;
};

/**
 * @param {Number} value
 * @param {Object} currency
 * @param {Object} separators
 * @param {Boolean} showCurrencySymbol
 * @returns {string}
 */
export const formatMoney = (value, currency, separators, showCurrencySymbol = true) => {
  let places;

  if (currency && !isNaN(parseFloat(currency.decimal_spaces)) && isFinite(currency.decimal_spaces)) {
    places = currency.decimal_spaces;
  } else {
    places = 0;
  }

  let code;
  let showOn = "right";

  if (currency && showCurrencySymbol === true) {
    code = currency.code;
    if (currency.code.toUpperCase() === "USD") {
      showOn = "left";
    }
  }

  let result = formatCurrency(
    isFinite(value) ? value : 0,
    separators.thousand_separator,
    separators.decimal_separator,
    true,
    places,
    "long",
    code,
    showOn
  );

  return result;
};

export const isValidCard = (cardExpiringYear, cardExpiringMonth) => {
  let newDate = new Date();
  let year = newDate.getFullYear();
  let month = newDate.getMonth() + 1;
  if (year < cardExpiringYear) {
    return true;
  }
  if (year === cardExpiringYear) {
    return month <= cardExpiringMonth;
  }
  return false;
};

export const isNumeric = (str) => {
  if (typeof str === "number") return true;
  if (typeof str != "string") return false;
  return !isNaN(str) && !isNaN(parseFloat(str));
};

export const projectClass = "Project";
export const taskClass = "Task";
export const userClass = "User";

export const rotateDays = (days = [], firstDayOfWeek = 0) =>
  firstDayOfWeek === 0 ? days : [...days.slice(1), days[0]];

export const formatFilters = (arr, translationMap = {}) => {
  if (!Array.isArray(arr)) return [];

  return arr.map((item) => ({
    id: item,
    name: Object.prototype.hasOwnProperty.call(translationMap, item) ? translationMap[item] : item,
  }));
};

export const modifyFilters = (filters, weekType = "week") => {
  let modifiedFilters = {
    ...filters,
  };
  if (filters.date) {
    switch (filters.date) {
      case "this_week":
        modifiedFilters.date = [
          moment().startOf(weekType).format("YYYY-MM-DD"),
          moment().endOf(weekType).format("YYYY-MM-DD"),
        ];
        break;
      case "last_week":
        modifiedFilters.date = [
          moment().subtract(7, "days").startOf(weekType).format("YYYY-MM-DD"),
          moment().subtract(7, "days").endOf(weekType).format("YYYY-MM-DD"),
        ];
        break;
      case "this_month":
        modifiedFilters.date = [
          moment().startOf("month").format("YYYY-MM-DD"),
          moment().endOf("month").format("YYYY-MM-DD"),
        ];
        break;
      case "last_month":
        modifiedFilters.date = [
          moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
          moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
        ];
        break;
      case "this_quarter":
        modifiedFilters.date = [
          moment().startOf("quarter").format("YYYY-MM-DD"),
          moment().endOf("quarter").format("YYYY-MM-DD"),
        ];
        break;
      case "last_quarter":
        modifiedFilters.date = [
          moment().subtract(1, "quarter").startOf("quarter").format("YYYY-MM-DD"),
          moment().subtract(1, "quarter").endOf("quarter").format("YYYY-MM-DD"),
        ];
        break;
      case "this_year":
        modifiedFilters.date = [
          moment().startOf("year").format("YYYY-MM-DD"),
          moment().endOf("year").format("YYYY-MM-DD"),
        ];
        break;
      case "last_year":
        modifiedFilters.date = [
          moment().subtract(1, "year").startOf("year").format("YYYY-MM-DD"),
          moment().subtract(1, "year").endOf("year").format("YYYY-MM-DD"),
        ];
        break;
    }
  }
  return modifiedFilters;
};

export const replaceAvatarSize = (avatar_url, size) => avatar_url?.replace("--SIZE--", size);

export const getEstimateStatusLabels = (t) => [
  { id: "draft", name: t("Draft") },
  { id: "sent", name: t("Sent") },
  { id: "won", name: t("Won") },
  { id: "lost", name: t("Lost") },
];

export const groupUsersByCompany = (companies, users, t) => {
  let grouped = companies.map((company) => {
    let companyUsers = users.filter((user) => company.members.includes(user.id));
    return {
      id: `company-${company.id}`,
      name: company.name,
      options: companyUsers,
    };
  });

  let usersWithoutCompany = users.filter((u) => {
    return !companies.filter((c) => c.members.includes(u.id)).length;
  });

  if (!usersWithoutCompany.length) {
    return grouped;
  }

  return [
    ...grouped,
    {
      id: "no-company",
      name: t ? t("Without company") : i18n.t("Without company"),
      options: usersWithoutCompany,
    },
  ];
};

export const calculateProgress = (dividend, divisor) => {
  if (divisor === 0 || !dividend || !divisor) {
    return 0;
  }

  return (dividend / divisor) * 100;
};

export const countriesWithRequiredRegion = ["US", "CA", "AU"];

export const objectHasRequiredKeys = (object, requiredKeys) => {
  return requiredKeys?.every((key) => {
    if (object) {
      const value = object[key];
      return value !== null && value !== undefined && value !== "";
    }
  });
};

export const objectHasAtLeastOneNonEmptyValueForKey = (object, requiredKeys) => {
  return requiredKeys?.some((key) => {
    if (object) {
      const value = object[key];
      return value !== null && value !== undefined && value !== "";
    }
  });
};

export const stripHtml = (html) => {
  if (!html) return "";
  const doc = new window.DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

export const trimFormValues = (values = {}) => {
  return Object.fromEntries(Object.entries(values).map(([k, v]) => [k, typeof v === "string" ? v.trim() : v]));
};

export const getProjectIntervals = (t) => [
  {
    id: "whole_project",
    name: t("Whole project"),
    period: "whole project",
  },
  {
    id: "yearly",
    name: t("Yearly"),
    period: "year",
  },
  {
    id: "monthly",
    name: t("Monthly"),
    period: "month",
  },
  {
    id: "weekly",
    name: t("Weekly"),
    period: "week",
  },
];
